import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Etiqueta de estado. Casi siempre "soft": fondo teñido + texto del propio
 * color, que es legible en ambos temas y no mancha la tabla de bloques solidos.
 * Las variantes solidas quedan para lo que debe interrumpir la lectura.
 *
 * Brandbook, lamina 20: «Mayusculas con tracking. Solido para lo resuelto,
 * contorno para estados». Eso es lo que hace el modificador `estado`: lo
 * RESUELTO —conciliado, al dia, presentado— va solido, porque es una conclusion;
 * lo que esta EN CURSO va de contorno, porque todavia se mueve. La diferencia no
 * es decorativa: en una tabla de cuatrocientas filas dice de un vistazo cuantas
 * cosas quedan abiertas.
 */
const badgeVariants = cva(
  [
    "inline-flex max-w-full items-center gap-1 rounded-sm border px-1.5 py-px",
    "text-2xs font-medium leading-[1.15rem]",
    "[&_svg]:size-3 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "border-border-strong/70 bg-transparent text-muted-foreground",
        destructive: "border-transparent bg-destructive text-destructive-foreground",
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-transparent bg-warning text-warning-foreground",

        /* Soft: el modo por defecto dentro de una tabla. */
        "destructive-soft": "border-destructive/25 bg-destructive-soft text-destructive",
        "success-soft": "border-success/25 bg-success-soft text-success",
        "warning-soft": "border-warning/30 bg-warning-soft text-warning",
        "brand-soft": "border-brand/25 bg-brand-soft text-brand",

        /*
          El «● Conciliado» del brandbook: el punto lleva el verde OK —que como
          texto no alcanza AA— y el texto lleva la lectura. Se compone con el
          caracter del punto en el contenido, no con un pseudoelemento, para que
          un lector de pantalla tambien lo anuncie.
        */
        conciliado: "border-success/25 bg-success-soft text-success [&_.punto-ok]:text-success-dot",

        /* Identidad de libro. Sobria: filete + tinte, nunca bloque saturado. */
        statutory: "border-book-statutory/30 bg-book-statutory/10 text-book-statutory",
        tax: "border-book-tax/30 bg-book-tax/10 text-book-tax",
        management: "border-book-management/30 bg-book-management/10 text-book-management",
      },
      mono: {
        true: "tabular-nums",
      },
      /**
       * Pildora de ESTADO: mayusculas con tracking abierto. Para «CONCILIADO»,
       * «AL DIA», «EN REVISION», «VENCE HOY». Uso breve — una etiqueta de
       * estado no es una frase.
       */
      estado: {
        true: "px-1.5 font-medium uppercase tracking-[0.06em]",
      },
    },
    defaultVariants: {
      variant: "outline",
    },
  },
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, mono, estado, ...props }: BadgeProps) {
  /*
    `estado` se DESESTRUCTURA aunque solo se use en `badgeVariants`. Si se
    quedara en `...props`, React lo escribiria como atributo del `<span>` y la
    consola avisa: «Received `true` for a non-boolean attribute `estado`».
    Mismo motivo que `mono` y `variant`: son variantes de cva, no HTML.
  */
  return <span className={cn(badgeVariants({ variant, mono, estado }), className)} {...props} />
}

export { Badge, badgeVariants }
